import { Typography } from 'antd';
import { memo } from 'react';
import { ICheckoutItem } from '../../../interfaces/ICheckout';
import { getFrequency, moneyMask } from '../helper';

type PricingSummaryProps = {
  firstItem: ICheckoutItem;
  isRecurrence: boolean;
  totalValue: number;
  maxInstallment: number;
  installmentValue: number;
  totalDiscountAmount: number;
  hasOrderBump: boolean;
  enrollmentFee: ICheckoutItem | undefined;
  getTotal: (withoutBump?: boolean) => number;
  hasPaymentMethodDiscount: boolean;
  lowestPrice: number;
  lowestPriceMethodLabel: string;
  otherMethodPrices: { method: string; net: number; label: string }[];
};

const RecurrencePeriod = memo(
  ({
    firstItem,
    variant,
  }: {
    firstItem: ICheckoutItem;
    variant: 'interval' | 'period';
  }) => {
    if (variant === 'interval') {
      return (
        <>
          a cada {firstItem?.recurrenceInterval}{' '}
          {getFrequency(firstItem?.recurrenceFrequency, firstItem?.recurrenceInterval)}{' '}
        </>
      );
    }

    const chargesQty = firstItem?.recurrenceChargesQuantity;
    if (chargesQty == null || chargesQty <= 0) return null;
    return (
      <>
        durante o período de {firstItem.recurrenceInterval * chargesQty}{' '}
        {getFrequency(firstItem?.recurrenceFrequency, firstItem.recurrenceInterval * chargesQty)}
        .
      </>
    );
  }
);

RecurrencePeriod.displayName = 'RecurrencePeriod';

const PricingSummary = memo(
  ({
    firstItem,
    isRecurrence,
    totalValue,
    maxInstallment,
    installmentValue,
    totalDiscountAmount,
    hasOrderBump,
    enrollmentFee,
    getTotal,
    hasPaymentMethodDiscount,
    lowestPrice,
    lowestPriceMethodLabel,
    otherMethodPrices,
  }: PricingSummaryProps) => {
    // Métodos com desconto além da âncora, listados inline: " · R$ 60,00 no PIX". Preço com
    // desconto sempre em negrito verde.
    const otherMethods = otherMethodPrices.map((price) => (
      <span key={price.method}>
        {' · '}
        <span className="font-semibold text-green-600">R$ {moneyMask.apply(price.net)}</span>{' '}
        {price.label}
      </span>
    ));

    if (!isRecurrence) {
      // Com preço por forma de pagamento, a âncora vira o MENOR preço (isca), coerente com a
      // prateleira logo abaixo; o parcelamento passa a ser a informação secundária.
      if (hasPaymentMethodDiscount) {
        return (
          <div className="text-gray-600">
            A partir de{' '}
            <span className="text-lg font-semibold text-green-600">R$ {moneyMask.apply(lowestPrice)}</span>{' '}
            {lowestPriceMethodLabel}
            {otherMethods}
            {maxInstallment > 1 && installmentValue > 0 && (
              <>
                {' · ou em até '}
                {maxInstallment}x de{' '}
                <span className="font-medium">R$ {moneyMask.apply(installmentValue)}</span>
              </>
            )}
            .
          </div>
        );
      }

      return (
        <div className="text-gray-600">
          Em até {maxInstallment}x de{' '}
          <span className="text-lg font-medium">R$ {moneyMask.apply(installmentValue)}</span> ou{' '}
          <span className="text-lg font-medium">
            {totalDiscountAmount === 0 && <>R$ {moneyMask.apply(totalValue)}</>}
            {totalDiscountAmount > 0 && (
              <>
                <s>R$ {moneyMask.apply(totalValue + totalDiscountAmount)}</s> R${' '}
                {moneyMask.apply(totalValue)}
              </>
            )}
          </span>{' '}
          à vista.
        </div>
      );
    }

    if (enrollmentFee && maxInstallment > 1 && installmentValue > 0) {
      const totalCents = getTotal() / 100;

      const subscriptionBlock = (
        <div className="mt-2">
          <h2 className="text-base font-medium">Assinatura</h2>
          <span className="text-lg text-black font-medium">
            {!firstItem?.couponItem && <>R$ {moneyMask.apply(firstItem?.amount / 100)}</>}
            {firstItem?.couponItem && (
              <>
                <s>R$ {moneyMask.apply(firstItem?.amount / 100)}</s> R${' '}
                {moneyMask.apply((firstItem?.couponItem?.amount ?? firstItem.amount) / 100)}
              </>
            )}
          </span>
          <div className="text-sm text-gray-500">
            <RecurrencePeriod firstItem={firstItem} variant="interval" />
            <RecurrencePeriod firstItem={firstItem} variant="period" />
          </div>
        </div>
      );

      // Com desconto por método, a âncora lidera com o MENOR preço (à vista/isca); o parcelamento
      // vira a informação secundária — coerente com o showcase avulso e sem repetir o à vista.
      if (hasPaymentMethodDiscount) {
        return (
          <div className="text-gray-600">
            <strong className="font-medium">{enrollmentFee.description}</strong>:{' '}
            <s>R$ {moneyMask.apply(totalCents)}</s>{' '}
            <span className="text-lg font-semibold text-green-600">R$ {moneyMask.apply(lowestPrice)}</span>{' '}
            {lowestPriceMethodLabel}
            {otherMethods}
            {' · ou em até '}
            {maxInstallment}x de{' '}
            <span className="font-medium">R$ {moneyMask.apply(installmentValue)}</span>.
            {subscriptionBlock}
          </div>
        );
      }

      return (
        <div className="text-gray-600">
          <strong className="font-medium">{enrollmentFee.description}</strong> em até {maxInstallment}x
          de <span className="text-lg font-medium text-black">R$ {moneyMask.apply(installmentValue)}</span>{' '}
          ou{' '}
          <span className="text-lg font-medium text-black">
            {totalDiscountAmount === 0 && <>R$ {moneyMask.apply(totalCents)}</>}
            {totalDiscountAmount > 0 && (
              <>
                <s>R$ {moneyMask.apply(totalCents + totalDiscountAmount)}</s> R${' '}
                {moneyMask.apply(totalCents)}
              </>
            )}
          </span>{' '}
          à vista.
          {subscriptionBlock}
        </div>
      );
    }

    if (enrollmentFee && maxInstallment === 1 && installmentValue > 0) {
      return (
        <div className="text-gray-600">
          <Typography.Title level={5} className="text-lg">
            {enrollmentFee.description?.toUpperCase()}
          </Typography.Title>
          <span className="text-lg font-medium">
            {hasPaymentMethodDiscount ? (
              <>
                <s>R$ {moneyMask.apply(getTotal() / 100)}</s>{' '}
                <span className="font-semibold text-green-600">R$ {moneyMask.apply(lowestPrice)}</span>
              </>
            ) : (
              <>R$ {moneyMask.apply(getTotal() / 100)}</>
            )}
          </span>{' '}
          à vista.
          {otherMethods}
          <div className="mt-2">
            <h2 className="text-base font-medium">Assinatura</h2>
            <span className="text-lg text-black font-medium">
              {!firstItem?.couponItem && <>R$ {moneyMask.apply(firstItem?.amount / 100)}</>}
              {firstItem?.couponItem && (
                <>
                  <s>R$ {moneyMask.apply(firstItem?.amount / 100)}</s> R${' '}
                  {moneyMask.apply((firstItem?.couponItem?.amount ?? firstItem.amount) / 100)}
                </>
              )}
            </span>
            <div className="text-sm text-gray-500">
              <RecurrencePeriod firstItem={firstItem} variant="interval" />
              <RecurrencePeriod firstItem={firstItem} variant="period" />
            </div>
          </div>
        </div>
      );
    }

    if (enrollmentFee && maxInstallment === 1 && installmentValue === 0) {
      return (
        <div className="text-gray-600">
          <Typography.Title level={5} className="text-lg">
            {enrollmentFee.description?.toUpperCase()}
          </Typography.Title>
          <span className="text-lg font-medium text-green-700">Grátis</span>
          <div className="mt-2">
            <h2 className="text-base font-medium">Assinatura</h2>
            <span className="text-lg text-black font-medium">
              {!firstItem?.couponItem && <>R$ {moneyMask.apply(firstItem?.amount / 100)}</>}
              {firstItem?.couponItem && (
                <>
                  <s>R$ {moneyMask.apply(firstItem?.amount / 100)}</s> R${' '}
                  {moneyMask.apply((firstItem?.couponItem?.amount ?? firstItem.amount) / 100)}
                </>
              )}
            </span>
            <div className="text-sm text-gray-500">
              <RecurrencePeriod firstItem={firstItem} variant="interval" />
              <RecurrencePeriod firstItem={firstItem} variant="period" />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="text-gray-600">
        <h2 className="text-base font-medium">Assinatura</h2>

        <span className="text-lg text-black font-medium">
          {!firstItem?.couponItem && <>R$ {moneyMask.apply(firstItem?.amount / 100)}</>}
          {firstItem?.couponItem && (
            <>
              <s>R$ {moneyMask.apply(firstItem?.amount / 100)}</s> R${' '}
              {moneyMask.apply((firstItem?.couponItem?.amount ?? firstItem.amount) / 100)}
            </>
          )}
        </span>

        <div className="text-sm text-gray-500">
          <RecurrencePeriod firstItem={firstItem} variant="interval" />
          <RecurrencePeriod firstItem={firstItem} variant="period" />
        </div>

        {hasOrderBump && !enrollmentFee && (
          <Typography.Text className="block mt-2 text-sm text-gray-500">
            Total: R$ {moneyMask.apply(getTotal() / 100)}{' '}
          </Typography.Text>
        )}
      </div>
    );
  }
);

PricingSummary.displayName = 'PricingSummary';

export default PricingSummary;
